import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../../api/client'
import type { MedFonte, MedFonti, MedRichiamo, MedStatoRichiamo } from '../../api/types'
import { useAuth } from '../../auth/AuthContext'
import { livelloModulo } from '../../auth/permessi'
import { useAsync } from '../../hooks/useAsync'
import { linguaCorrente, t } from '../../i18n/lingua'
import { Card, ErrorBanner, Spinner, StatCard, formatDateTime, formatDay } from '../common'
import { DataTable } from '../DataTable'
import { PosizioneDichiarata, testoDichiarata } from './Disponibilita'
import { Finestra } from '../ModificheGitlab'
import { BadgeStatoMed } from './comuni'

/**
 * CENSO MED (D9): the regulators' recalls and field safety notices on the
 * models of the hospital's devices, with the hospital's handling of each, the
 * CISA ICSMA cybersecurity advisories on its devices, and «Paese e basi dati»:
 * the hospital's country switches on the public sources that serve it, and
 * each can be switched on or off by hand.
 */

const STATI: MedStatoRichiamo[] = ['new', 'in_progress', 'done', 'not_applicable']

export function etichettaStatoRichiamo(s: string): string {
  const e: Record<string, string> = {
    new: t('Da valutare'), in_progress: t('In gestione'), done: t('Gestito'), not_applicable: t('Non ci riguarda'),
  }
  return e[s] ?? s
}

export function BadgeStatoRichiamo({ stato }: { stato: string }) {
  const tono: Record<string, string> = { new: 'error', in_progress: 'warn', done: 'ok', not_applicable: 'muted' }
  return <span className={`badge badge--${tono[stato] ?? 'muted'}`}>{etichettaStatoRichiamo(stato)}</span>
}

/** The authority of a notice, by the id of its source. */
export function etichettaAutorita(id: string): string {
  const e: Record<string, string> = {
    openfda: t('FDA (Stati Uniti)'), mhra: t('MHRA (Regno Unito)'), health_canada: t('Health Canada (Canada)'),
    bfarm: t('BfArM (Germania)'), ansm: t('ANSM (Francia)'), swissmedic: t('Swissmedic (Svizzera)'),
  }
  return e[id] ?? id
}

/** A country's name in the console's language («IT» → «Italia»). */
export function nomePaese(codice: string): string {
  if (codice === 'EU') return t('Unione europea')
  try {
    return new Intl.DisplayNames([linguaCorrente()], { type: 'region' }).of(codice) ?? codice
  } catch {
    return codice
  }
}

function useScrive(): boolean {
  const { principal } = useAuth()
  return livelloModulo(principal, 'medical') === 'write'
}

function FinestraRichiamo({ r, scrive, onChiudi, onFatto }: {
  r: MedRichiamo
  scrive: boolean
  onChiudi: () => void
  onFatto: () => void
}) {
  const [stato, setStato] = useState<MedStatoRichiamo>(r.status)
  const [nota, setNota] = useState(r.note)
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)

  async function salva() {
    setErrore(null)
    setLavoro(true)
    try {
      await api.medRecallSave(r.id, stato, nota)
      onFatto()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  const fatti: [string, string][] = [
    [t('Autorità'), etichettaAutorita(r.authority)],
    [t('Pubblicato il'), formatDay(r.posted)],
    [t('Avviato il'), formatDay(r.initiated)],
    [t('Classe'), r.classification || '—'],
    [t('Stato presso l’autorità'), r.fda_status || '—'],
    [t('Ditta'), r.firm || '—'],
    [t('Causa'), r.root_cause || '—'],
  ]
  return (
    <Finestra titolo={`${r.number} — ${etichettaAutorita(r.authority)}`} sotto={r.title || undefined} onChiudi={onChiudi}>
      <ErrorBanner error={errore} />
      <div className="form-dialog">
        <section className="form__sezione">
          <h4>{t('Cosa dice l’autorità')}</h4>
          <dl className="med-fatti">
            {fatti.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
          </dl>
          <p className="field__hint">{t('I testi sono quelli pubblicati dall’autorità, nella sua lingua.')}</p>
          <p><strong>{t('Prodotto')}</strong><br /><span className="med-testo-lungo">{r.product_description || '—'}</span></p>
          <p><strong>{t('Motivo')}</strong><br /><span className="med-testo-lungo">{r.reason || '—'}</span></p>
          {r.action && <p><strong>{t('Azione richiesta dalla ditta')}</strong><br /><span className="med-testo-lungo">{r.action}</span></p>}
          {r.url && <p><a href={r.url} target="_blank" rel="noreferrer noopener">{t('Apri il richiamo sul sito dell’autorità')}</a></p>}
        </section>
        <section className="form__sezione">
          <h4>{t('I vostri dispositivi interessati ({n})', { n: r.devices })}</h4>
          <p className="field__hint">{t('Il richiamo riguarda il modello: verificate con il fornitore numero di serie e versione dei vostri esemplari.')}</p>
          <DataTable
            rows={r.device_list}
            rowKey={(d) => d.id}
            paginazione={r.device_list.length > 25 ? undefined : false}
            strumenti={false}
            columns={[
              { key: 'name', header: t('Dispositivo'), value: (d) => d.name, render: (d) => <Link to={`/medical/devices/${d.id}`}>{d.name || '—'}</Link> },
              { key: 'inventory_number', header: t('N. inventario'), mono: true, value: (d) => d.inventory_number ?? '' },
              { key: 'serial_number', header: t('N. di serie'), mono: true, value: (d) => d.serial_number ?? '' },
              { key: 'model', header: t('Modello'), value: (d) => d.model },
              { key: 'org_unit', header: t('Reparto'), value: (d) => d.org_unit ?? '' },
              // Where it is now, to reach it: what the device declares, else where the network sees it.
              { key: 'where_now', header: t('Dove si trova adesso'),
                value: (d) => testoDichiarata(d.where_now?.declared) || [d.where_now?.switch_name, d.where_now?.port, d.where_now?.vlan_name].filter(Boolean).join(' · '),
                render: (d) => d.where_now?.declared
                  ? <PosizioneDichiarata p={d.where_now.declared} />
                  : d.where_now?.switch_name
                    ? <>{d.where_now.switch_name} <span className="mono">{d.where_now.port}</span>{d.where_now.vlan_name ? <small className="text-muted"> · {d.where_now.vlan_name}</small> : null}</>
                    : <span className="text-muted">—</span> },
              { key: 'status', header: t('Stato'), value: (d) => d.status, render: (d) => <BadgeStatoMed stato={d.status} /> },
            ]}
          />
        </section>
        <section className="form__sezione">
          <h4>{t('Come lo gestite')}</h4>
          <div className="form-grid">
            <label className="field span-2"><span className="field__label">{t('Stato')}</span>
              <select value={stato} onChange={(e) => setStato(e.target.value as MedStatoRichiamo)} disabled={!scrive}>
                {STATI.map((s) => <option key={s} value={s}>{etichettaStatoRichiamo(s)}</option>)}
              </select></label>
            <label className="field span-6"><span className="field__label">{t('Note')}</span>
              <textarea rows={3} value={nota} onChange={(e) => setNota(e.target.value)} maxLength={4000} disabled={!scrive}
                placeholder={t('per esempio: sentito il fornitore, aggiornamento software pianificato il …')} /></label>
          </div>
          {r.updated_by && (
            <p className="field__hint">{t('Ultima modifica: {chi}, {quando}', { chi: r.updated_by, quando: formatDateTime(r.updated_at) })}</p>
          )}
        </section>
        <div className="form-dialog__azioni">
          <button type="button" className="secondary" onClick={onChiudi} disabled={lavoro}>{t('Chiudi')}</button>
          {scrive && <button type="button" onClick={() => void salva()} disabled={lavoro}>{t('Salva')}</button>}
        </div>
      </div>
    </Finestra>
  )
}

export function VistaRichiami() {
  const scrive = useScrive()
  const dati = useAsync(() => api.medRecalls(), [])
  const fonti = useAsync(() => api.medSources(), [])
  const [aperto, setAperto] = useState<string | null>(null)
  const [avviso, setAvviso] = useState<string | null>(null)
  const x = dati.data
  const richiamo = useMemo(() => x?.recalls.find((r) => r.id === aperto) ?? null, [x, aperto])
  if (dati.loading && !x) return <Spinner />
  if (!x) return <ErrorBanner error={dati.error} />
  const attive = (fonti.data?.sources ?? []).filter((f) => f.active && f.contents.includes('recalls'))

  return (
    <>
      <div className="wazuh-kpi">
        <StatCard label={t('Richiami da gestire')} value={x.counts.to_handle} tone={x.counts.to_handle ? 'warn' : 'ok'} />
        <StatCard label={t('Da valutare')} value={x.counts.new} tone={x.counts.new ? 'error' : 'ok'} />
        <StatCard label={t('Di classe I')} value={x.counts.class_1} tone={x.counts.class_1 ? 'error' : 'ok'}
          hint={t('la classe più grave (FDA)')} />
        <StatCard label={t('Dispositivi interessati')} value={x.counts.devices} />
        <StatCard label={t('Avvisi informatici aperti')} value={x.counts.advisories_open} tone={x.counts.advisories_open ? 'error' : 'ok'}
          hint={t('CISA ICSMA')} />
      </div>
      <p className="page__subtitle">
        {attive.length
          ? t('Richiami letti da: {fonti}.', { fonti: attive.map((f) => etichettaAutorita(f.id)).join(', ') })
          : t('Nessuna fonte di richiami attiva per il vostro ospedale.')}{' '}
        <Link to="/medical/sources">{t('Paese e basi dati')}</Link>
      </p>
      <Card title={t('Richiami e avvisi di sicurezza delle autorità')}>
        <DataTable
          id="medical-recalls"
          rows={x.recalls}
          rowKey={(r) => r.id}
          initialSort={{ key: 'posted', dir: 'desc' }}
          conteggio={{ nome: t('richiami') }}
          nomeEsportazione="elettromedicali-richiami"
          emptyText={t('Nessun richiamo sui modelli dei vostri elettromedicali. Si cercano per modello esatto: i dispositivi di cui si conosce solo la serie non si abbinano.')}
          columns={[
            { key: 'number', header: t('Richiamo'), mono: true, value: (r) => r.number,
              render: (r) => <button type="button" className="link" onClick={() => setAperto(r.id)}>{r.number}</button> },
            { key: 'posted', header: t('Pubblicato'), type: 'date', value: (r) => r.posted ?? '', render: (r) => formatDay(r.posted) },
            { key: 'authority', header: t('Autorità'), filter: 'select', value: (r) => etichettaAutorita(r.authority) },
            { key: 'classification', header: t('Classe'), filter: 'select', value: (r) => r.classification,
              render: (r) => (r.classification ? <span className={`badge badge--${r.classification === 'Class I' ? 'error' : 'warn'}`}>{r.classification}</span> : '—') },
            { key: 'models', header: t('Modelli'), value: (r) => r.models.map((m) => m.model).join(', ') },
            { key: 'devices', header: t('Dispositivi'), type: 'number', align: 'right', value: (r) => r.devices },
            { key: 'product', header: t('Prodotto'), value: (r) => r.title || r.product_description,
              render: (r) => <span className="med-testo-corto" title={r.product_description}>{r.title || r.product_description}</span> },
            { key: 'reason', header: t('Motivo'), value: (r) => r.reason, hidden: true },
            { key: 'firm', header: t('Ditta'), value: (r) => r.firm, hidden: true },
            { key: 'status', header: t('Gestione'), filter: 'select', value: (r) => etichettaStatoRichiamo(r.status),
              render: (r) => <BadgeStatoRichiamo stato={r.status} /> },
          ]}
        />
      </Card>
      {x.maker_notices.length > 0 && (
        <Card title={t('Altri avvisi sui vostri produttori ({n})', { n: x.maker_notices.length })}>
          <p className="field__hint" style={{ marginTop: 0 }}>
            {t('Avvisi degli ultimi 180 giorni che non indicano un modello del catalogo ma vengono dai produttori dei vostri elettromedicali: spesso nominano solo il prodotto e i modelli stanno nella lettera del fabbricante. Da controllare; non arrivano per mail.')}
          </p>
          <DataTable
            id="medical-maker-notices"
            rows={x.maker_notices}
            rowKey={(a) => a.id}
            initialSort={{ key: 'posted', dir: 'desc' }}
            conteggio={{ nome: t('avvisi') }}
            columns={[
              { key: 'number', header: t('Avviso'), mono: true, value: (a) => a.number,
                render: (a) => (a.url ? <a href={a.url} target="_blank" rel="noreferrer noopener">{a.number}</a> : a.number) },
              { key: 'posted', header: t('Pubblicato'), type: 'date', value: (a) => a.posted ?? '', render: (a) => formatDay(a.posted) },
              { key: 'authority', header: t('Autorità'), filter: 'select', value: (a) => etichettaAutorita(a.authority) },
              { key: 'firm', header: t('Ditta'), filter: 'select', value: (a) => a.firm || a.firm_key },
              { key: 'title', header: t('Prodotto'), value: (a) => a.title,
                render: (a) => <span className="med-testo-corto" title={a.product_description}>{a.title}</span> },
              { key: 'classification', header: t('Tipo'), value: (a) => a.classification, hidden: true },
            ]}
          />
        </Card>
      )}
      <Card title={t('Avvisi di sicurezza informatica (CISA ICSMA)')}>
        <DataTable
          id="medical-icsma"
          rows={x.advisories}
          rowKey={(a) => a.rule}
          initialSort={{ key: 'first_seen', dir: 'desc' }}
          conteggio={{ nome: t('avvisi') }}
          emptyText={t('Nessun avviso CISA sui vostri elettromedicali.')}
          expanded={(a) => a.rule === avviso}
          expand={(a) => (
            <DataTable rows={a.device_list} rowKey={(d) => d.id} paginazione={false} strumenti={false}
              columns={[
                { key: 'name', header: t('Dispositivo'), value: (d) => d.name, render: (d) => <Link to={`/medical/devices/${d.id}`}>{d.name || '—'}</Link> },
                { key: 'inventory_number', header: t('N. inventario'), mono: true, value: (d) => d.inventory_number ?? '' },
                { key: 'model', header: t('Modello'), value: (d) => d.model },
                { key: 'accepted', header: t('Stato'), value: (d) => (d.accepted ? t('accettato') : t('aperto')) },
              ]} />
          )}
          columns={[
            { key: 'advisory', header: t('Avviso'), mono: true, value: (a) => a.advisory,
              render: (a) => (a.url ? <a href={a.url} target="_blank" rel="noreferrer noopener">{a.advisory}</a> : a.advisory) },
            { key: 'title', header: t('Titolo'), value: (a) => a.title },
            { key: 'severity', header: t('Gravità'), filter: 'select', value: (a) => a.severity },
            { key: 'devices', header: t('Dispositivi'), type: 'number', align: 'right', value: (a) => a.devices },
            { key: 'open', header: t('Aperti'), type: 'number', align: 'right', value: (a) => a.open },
            { key: 'first_seen', header: t('Visto dal'), type: 'date', value: (a) => a.first_seen ?? '', render: (a) => formatDay(a.first_seen) },
            { key: 'cves', header: t('CVE'), value: (a) => a.cves.join(', '), hidden: true },
            { key: 'azioni', header: '', sort: false, searchable: false,
              render: (a) => (
                <button type="button" className="secondary" onClick={() => setAvviso(avviso === a.rule ? null : a.rule)}>
                  {avviso === a.rule ? t('Chiudi') : t('Dispositivi')}
                </button>
              ) },
          ]}
        />
      </Card>
      {richiamo && (
        <FinestraRichiamo r={richiamo} scrive={scrive} onChiudi={() => setAperto(null)}
          onFatto={() => { setAperto(null); void dati.reload() }} />
      )}
    </>
  )
}

function etichettaContenuto(c: string): string {
  const e: Record<string, string> = {
    classification: t('classificazione'), registry: t('identificazione dei modelli'), recalls: t('richiami'),
    cyber: t('sicurezza informatica'),
  }
  return e[c] ?? c
}

export function VistaFonti() {
  const scrive = useScrive()
  const dati = useAsync(() => api.medSources(), [])
  const [paese, setPaese] = useState<string | null>(null)
  const [scelte, setScelte] = useState<Record<string, boolean>>({})
  const [errore, setErrore] = useState<unknown>(null)
  const [salvato, setSalvato] = useState(false)
  const [lavoro, setLavoro] = useState(false)
  const x: MedFonti | null = dati.data
  if (dati.loading && !x) return <Spinner />
  if (!x) return <ErrorBanner error={dati.error} />
  const scelto = paese ?? x.country
  const paesi = [...new Set([...x.countries, x.country])].sort((a, b) => nomePaese(a).localeCompare(nomePaese(b)))
  const attiva = (f: MedFonte) => (f.locked ? true : scelte[f.id] ?? (paese === null || paese === x.country ? f.active : f.recommended))

  async function salva() {
    setErrore(null)
    setLavoro(true)
    try {
      const sources = Object.fromEntries((x?.sources ?? []).filter((f) => !f.locked).map((f) => [f.id, attiva(f)]))
      await api.medSourcesSave(scelto, sources)
      setPaese(null)
      setScelte({})
      setSalvato(true)
      await dati.reload()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  return (
    <>
      <ErrorBanner error={errore} />
      {!x.chosen && (
        <div className="banner banner--info">{t('Il Paese non è ancora stato scelto: CENSO assume l’Italia. Sceglietelo per attivare le basi dati giuste.')}</div>
      )}
      {salvato && <div className="banner banner--ok">{t('Salvato: le schede e i richiami seguono le nuove fonti.')}</div>}
      <Card title={t('Paese dell’ospedale')}>
        <div className="form-grid">
          <label className="field span-3"><span className="field__label">{t('Paese')}</span>
            <select value={scelto} disabled={!scrive}
              onChange={(e) => { setPaese(e.target.value); setScelte({}); setSalvato(false) }}>
              {paesi.map((p) => <option key={p} value={p}>{nomePaese(p)}</option>)}
            </select>
            <span className="field__hint">{t('Il Paese accende le basi dati che gli servono; ognuna si può poi accendere o spegnere a mano.')}</span>
          </label>
        </div>
      </Card>
      <Card title={t('Basi dati pubbliche')}>
        <DataTable
          rows={x.sources}
          rowKey={(f) => f.id}
          paginazione={false}
          strumenti={false}
          columns={[
            { key: 'active', header: t('Attiva'), sort: false, searchable: false,
              render: (f) => (
                <input type="checkbox" aria-label={t('Attiva {fonte}', { fonte: f.name })} checked={attiva(f)}
                  disabled={!scrive || f.locked}
                  onChange={(e) => { setScelte({ ...scelte, [f.id]: e.target.checked }); setSalvato(false) }} />
              ) },
            { key: 'name', header: t('Fonte'), value: (f) => t(f.name),
              render: (f) => (
                <span>{t(f.name)}{f.locked && <small className="muted"> · {t('sempre attiva')}</small>}</span>
              ) },
            { key: 'country', header: t('Paese della fonte'), value: (f) => nomePaese(f.country) },
            { key: 'contents', header: t('Cosa dà'), value: (f) => f.contents.map(etichettaContenuto).join(', ') },
            { key: 'recommended', header: t('Per il vostro Paese'), value: (f) => (f.recommended ? t('consigliata') : ''),
              render: (f) => (f.recommended ? <span className="badge badge--ok">{t('consigliata')}</span> : '—') },
            { key: 'last_read', header: t('Ultima lettura'), type: 'date', value: (f) => f.last_read ?? '', render: (f) => formatDateTime(f.last_read) },
            { key: 'host', header: t('Indirizzo'), mono: true, value: (f) => f.host, hidden: true },
          ]}
        />
        <p className="field__hint">
          {t('Le fonti si leggono una volta per tutta la piattaforma; ogni ospedale vede solo quelle attive per sé. Una fonte che nessun ospedale usa non viene scaricata.')}
        </p>
        {scrive && (
          <div className="form-dialog__azioni">
            <button type="button" onClick={() => void salva()} disabled={lavoro}>{t('Salva')}</button>
          </div>
        )}
      </Card>
    </>
  )
}
